import _ from 'lodash'
import { proxy } from 'comlink/dist/esm/comlink.js' // eslint-disable-line no-restricted-syntax
import { withDependencies, named, multi, optional } from '@wix/thunderbolt-ioc'
import {
	CarmiInstance,
	SiteFeatureConfigSymbol,
	PlatformEnvDataProvider,
	PlatformEnvDataProviderSymbol,
	PlatformSiteConfig,
	WixCodeSdkHandlersProviderSym,
	SdkHandlersProvider,
	PageAssetsLoaderSymbol,
	IPageAssetsLoader,
	PlatformModel,
	PartialFeaturesResponse,
	CurrentRouteInfoSymbol,
	PlatformEnvData,
	PlatformDsApi,
	RunPlatformOptions,
	IAppWillUnmountHandler,
} from '@wix/thunderbolt-symbols'
import { IPageProvider, LogicalReflectorSymbol } from 'feature-pages'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import type { PlatformClientWorkerAPI } from '../core/types'
import type { ICurrentRouteInfo } from 'feature-router'
import { createBootstrapData } from '../viewer/createBootstrapData'
import { name } from '../symbols'
import { platformUpdatesFunctionsNames } from '../constants'
import { IWorkerIframeWrapperManager, WorkerIframeWrapperManagerSymbol } from './worker-iframe-wrapper/workerIframeWrapperManager'
import { ILightboxUtils, LightboxUtilsSymbol } from 'feature-lightbox'

export const platformDS = withDependencies(
	[
		CarmiInstance,
		multi(WixCodeSdkHandlersProviderSym),
		multi(PlatformEnvDataProviderSymbol),
		named(SiteFeatureConfigSymbol, name),
		LogicalReflectorSymbol,
		WorkerIframeWrapperManagerSymbol,
		PageAssetsLoaderSymbol,
		CurrentRouteInfoSymbol,
		optional(LightboxUtilsSymbol),
	],
	(
		instance: DSCarmi,
		siteHandlersProviders: Array<SdkHandlersProvider<any>>,
		platformEnvDataProviders: Array<PlatformEnvDataProvider>,
		platformSiteConfig: PlatformSiteConfig,
		pageProvider: IPageProvider,
		workerIframeWrapperManager: IWorkerIframeWrapperManager,
		pageAssetsLoader: IPageAssetsLoader,
		currentRouteInfo: ICurrentRouteInfo,
		lightboxUtils: ILightboxUtils
	): PlatformDsApi & IAppWillUnmountHandler => {
		const getPageInfo = (options?: RunPlatformOptions) => {
			if (options?.pageInfo) {
				return options!.pageInfo
			}

			const currentLightboxId = lightboxUtils.getCurrentLightboxId()
			if (currentLightboxId) {
				return { contextId: currentLightboxId, pageId: currentLightboxId }
			}

			return currentRouteInfo.getCurrentRouteInfo()!
		}

		const siteHandlers = siteHandlersProviders.map((siteHandlerProvider) => siteHandlerProvider.getSdkHandlers())

		async function runPlatform({ contextId, pageId }: { contextId: string; pageId: string }, options?: Omit<RunPlatformOptions, 'pageInfo'>) {
			let deferPlatformUpdates = options?.deferPlatformUpdates
			const deferredUpdates: Array<() => unknown> = []

			const siteConfigBootstrapData = platformSiteConfig.bootstrapData
			if (!siteConfigBootstrapData || platformSiteConfig.debug.disablePlatform) {
				return { flushPlatformUpdates: _.noop }
			}

			const pageRef = await pageProvider(contextId, pageId)

			const pageHandlersProviders = pageRef.getAllImplementersOf<SdkHandlersProvider<any>>(WixCodeSdkHandlersProviderSym)
			const pageHandlers = pageHandlersProviders.map((pageHandlerProvider) => pageHandlerProvider.getSdkHandlers())
			const sdkHandlers = Object.assign({}, ...pageHandlers, ...siteHandlers)

			const platformEnvData = Object.assign({}, ...platformEnvDataProviders.map((envApiProvider) => envApiProvider.platformEnvData(pageId))) as PlatformEnvData

			const { initPlatformOnSite, runPlatformOnPage }: PlatformClientWorkerAPI = await workerIframeWrapperManager.createIframeWorker({
				wixCodeBootstrapData: siteConfigBootstrapData.wixCodeBootstrapData,
			})

			const invokeSiteHandler = async (path: string, ...args: Array<unknown>) => {
				const handler = _.get(sdkHandlers, path)
				if (!handler) {
					console.log(`handler ${path} is not implemented`)
					return
				}
				const res = await handler(...args)
				return _.isFunction(res) ? proxy(res) : res
			}

			const bootstrapData = createBootstrapData({
				platformEnvData,
				platformBootstrapData: siteConfigBootstrapData,
				siteFeaturesConfigs: instance.siteFeaturesConfigs,
				currentContextId: contextId,
				currentPageId: pageId,
				experiments: {},
			})

			await initPlatformOnSite(bootstrapData, proxy(invokeSiteHandler))

			const getAssetsModel = async (assetName: 'features' | 'platform', isMasterPage: boolean): Promise<PlatformModel | PartialFeaturesResponse> => {
				const assets = pageAssetsLoader.load(isMasterPage ? 'masterPage' : pageId)
				const pageFeatureConfigs = await assets.props

				if (assetName === 'platform') {
					const { livePreviewOptions } = platformEnvData

					if (!livePreviewOptions!.apps?.length) {
						return pageFeatureConfigs.platform
					}

					const { applications } = pageFeatureConfigs.platform
					const filteredApplications = livePreviewOptions!.apps.reduce((acc, appDefinitionId) => {
						if (applications[appDefinitionId]) {
							acc[appDefinitionId] = applications[appDefinitionId]
						}
						return acc
					}, {} as PlatformModel['applications'])

					return {
						...pageFeatureConfigs.platform,
						applications: filteredApplications,
					}
				}

				const components = await assets.components
				const features = await assets.features
				return {
					props: {
						...pageFeatureConfigs,
						render: {
							// using JSON.parse(JSON.stringify()) to remove unserializable props
							compProps: pageFeatureConfigs.render.compProps ? JSON.parse(JSON.stringify(pageFeatureConfigs.render.compProps)) : undefined,
						},
					},
					structure: {
						components: { ...components },
						features: [...features],
					},
				}
			}

			const invokeViewerHandler = async (_pageId: string, path: Array<string>, ...args: any) => {
				const handler = _.get(sdkHandlers, path)
				if (!handler) {
					console.log(`handler ${path.join('.')} is not implemented`)
					return
				}

				const action = () => handler(...args)
				const functionName = _.last(path) as string

				if (deferPlatformUpdates && platformUpdatesFunctionsNames.includes(functionName)) {
					// wait for platform to finish setting all ooi props before rendering the ooi component
					deferredUpdates.push(action)
					return
				}

				const res = await action()
				return _.isFunction(res) ? proxy(res) : res
			}

			await runPlatformOnPage(bootstrapData, proxy(invokeViewerHandler), proxy(getAssetsModel))

			return {
				flushPlatformUpdates: () => {
					deferPlatformUpdates = false
					deferredUpdates.map((action) => action())
				},
			}
		}

		return {
			async runPlatform(options) {
				const pageInfo = getPageInfo(options)
				return runPlatform(pageInfo, options)
			},
			appWillUnmount() {
				workerIframeWrapperManager.disposeIframeWorker()
			},
		}
	}
)
